import { Anchor, Button, Group, Text } from '@mantine/core';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconMail, IconMessage } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import type { Account } from '../model.ts';

const TITLE_STYLE = { color: 'gray', fontWeight: 'normal' };

const OLD_DASHBOARD_URL = import.meta.env.VITE_BASE_URL;

const buildEmailOtpUrl = (email: string) =>
  `${OLD_DASHBOARD_URL}/cockpit_latest_email_otps?${new URLSearchParams({
    utf8: '✓',
    email,
    commit: '查詢',
  })}`;

const buildSmsOtpUrl = (id: string, phoneNumber: string) =>
  `${OLD_DASHBOARD_URL}/cockpit_latest_sms_otps?${new URLSearchParams({
    utf8: '✓',
    id,
    phone_number: phoneNumber,
    commit: '查詢',
  })}`;

const OtpLinkButton = ({
  label,
  icon,
  href,
}: {
  label: string;
  icon: ReactNode;
  href: string | null;
}) =>
  href ? (
    <Button
      size="compact-sm"
      variant="light"
      leftSection={icon}
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </Button>
  ) : (
    <Button size="compact-sm" variant="light" leftSection={icon} disabled>
      {label}
    </Button>
  );

const columns: DataTableColumn<Account>[] = [
  {
    title: 'Email',
    titleStyle: TITLE_STYLE,
    accessor: 'email',
    render: ({ id, email }) => (
      <Anchor
        component={Link}
        to={`/driver-management/drivers/${id}`}
        size="sm"
        underline="always"
      >
        {email}
      </Anchor>
    ),
  },
  {
    title: '手機號碼',
    titleStyle: TITLE_STYLE,
    accessor: 'phoneNumber',
    render: ({ phoneNumber }) => <Text size="sm">{phoneNumber ?? '-'}</Text>,
  },
  {
    title: '建立時間',
    titleStyle: TITLE_STYLE,
    accessor: 'createDateTime',
    render: ({ createDateTime }) => (
      <Text size="sm">
        {format(parseISO(createDateTime), 'yyyy/MM/dd HH:mm:ss')}
      </Text>
    ),
  },
  {
    title: '操作',
    titleStyle: TITLE_STYLE,
    accessor: 'actions',
    textAlign: 'center',
    render: ({ id, email, phoneNumber }) => (
      <Group gap="xs" justify="center" wrap="nowrap">
        <OtpLinkButton
          label="Email OTPs"
          icon={<IconMail size={16} />}
          href={buildEmailOtpUrl(email)}
        />
        <OtpLinkButton
          label="SMS OTPs"
          icon={<IconMessage size={16} />}
          href={phoneNumber ? buildSmsOtpUrl(id, phoneNumber) : null}
        />
      </Group>
    ),
  },
];

const DriverListTable = ({
  data,
  page,
  pageSize,
  totalRecords,
  fetching,
  onPageChange,
  emptyState,
  customLoader,
}: {
  data: Account[];
  page: number;
  pageSize: number;
  totalRecords: number;
  fetching: boolean;
  onPageChange: (page: number) => void;
  emptyState?: ReactNode;
  customLoader?: ReactNode;
}) => (
  <DataTable
    idAccessor="id"
    highlightOnHover
    verticalSpacing="md"
    minHeight={`calc(100vh - 250px)`}
    records={data}
    columns={columns}
    fetching={fetching}
    recordsPerPage={pageSize}
    page={page}
    totalRecords={totalRecords}
    emptyState={emptyState}
    customLoader={customLoader}
    paginationText={({ from, to, totalRecords }) =>
      `顯示 ${from} - ${to} 筆駕駛人資料，共 ${totalRecords} 筆`
    }
    onPageChange={onPageChange}
    styles={{ pagination: { color: 'gray' } }}
  />
);

export default DriverListTable;
